<template>
  <div class="bank-account-form">
    <div v-if="id" class="bank-account-form-summary">
      <div class="bank-account-form-summary-identity">
        <div class="bank-account-form-summary-logo">
          <img v-if="showLogo" :src="bankAccount.urlLogo" @error="logoError = true" />
          <i v-else class="fa-solid fa-building-columns"></i>
        </div>
        <div class="bank-account-form-summary-info">
          <span class="bank-account-form-summary-name">{{ summaryName }}</span>
          <span v-if="accountLabel" class="bank-account-form-summary-account">{{ accountLabel }}</span>
          <DotLabel v-if="showOpenFinance" size="small"
            :title="bankAccount.pluggyId ? 'Open Finance conectado' : 'Open Finance não conectado'"
            :color="bankAccount.pluggyId ? 'success' : 'disabled'" />
        </div>
      </div>

      <div class="bank-account-form-summary-balance">
        <span class="bank-account-form-summary-balance-label">Saldo Atual</span>
        <span v-if="hideBalance" class="bank-account-form-summary-balance-hidden">••••••</span>
        <span v-else class="bank-account-form-summary-balance-value" :class="{
          'bank-account-form-positive': bankAccount.accountBalance >= 0,
          'bank-account-form-negative': bankAccount.accountBalance < 0,
        }">{{ formatCurrency(bankAccount.accountBalance) }}</span>
        <Tooltip :title="hideBalance ? 'Mostrar Saldo' : 'Ocultar Saldo'">
          <span class="bank-account-form-summary-balance-toggle" @click="toggleBalance">
            <i :class="hideBalance ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          </span>
        </Tooltip>
      </div>
    </div>

    <div class="bank-account-form-card">
      <div class="bank-account-form-section">
        <span class="bank-account-form-section-title">Tipo de Conta</span>
        <b-row>
          <b-col xs="12" sm="12" md="6" lg="4" xl="4">
            <div class="bank-account-form-type" :class="{ 'bank-account-form-type-selected': isBankAccount }"
              tabindex="0" @click="selectType(1)" @keyup.enter="selectType(1)">
              <div class="bank-account-form-type-icon">
                <i class="fa-solid fa-building-columns"></i>
              </div>
              <div class="bank-account-form-type-info">
                <span class="bank-account-form-type-title">Conta Bancária</span>
              </div>
              <i v-if="isBankAccount" class="fa-solid fa-circle-check bank-account-form-type-check"></i>
            </div>
          </b-col>
          <b-col xs="12" sm="12" md="6" lg="4" xl="4">
            <div class="bank-account-form-type" :class="{ 'bank-account-form-type-selected': !isBankAccount }"
              tabindex="0" @click="selectType(2)" @keyup.enter="selectType(2)">
              <div class="bank-account-form-type-icon">
                <i class="fa-solid fa-money-bill-wave"></i>
              </div>
              <div class="bank-account-form-type-info">
                <span class="bank-account-form-type-title">Caixa Interno</span>
              </div>
              <i v-if="!isBankAccount" class="fa-solid fa-circle-check bank-account-form-type-check"></i>
            </div>
          </b-col>
        </b-row>
      </div>

      <div v-if="isBankAccount" class="bank-account-form-section">
        <span class="bank-account-form-section-title">Dados Bancários</span>
        <b-row>
          <b-col xs="12" sm="12" md="12" lg="6" xl="6">
            <Select title="Banco" field="bank" :formName="formName" :required="isBankAccount"
              url="/api/v1/financial/bank/select-all" v-model="bankAccount.bank" nameNewRegister="bankCreateUpdate"
              titleNewRegister="Banco" :widthNewRegister="500" :heightNewRegister="250">
              <BankCreateUpdate :registerInSelect="true" v-model="bankAccount.bank" />
            </Select>
          </b-col>
        </b-row>
        <b-row>
          <b-col xs="8" sm="8" md="6" lg="3" xl="3">
            <InputText title="Agência" field="agency" :formName="formName" :required="isBankAccount" :maxLength="10"
              :mask="maskAgency" v-model="bankAccount.agency" />
          </b-col>
          <b-col xs="4" sm="4" md="3" lg="2" xl="2">
            <InputText title="Digito" field="agencyDigit" :formName="formName" :required="false" :maxLength="2"
              :mask="maskDigit" v-model="bankAccount.agencyDigit" />
          </b-col>
          <b-col xs="8" sm="8" md="6" lg="4" xl="4">
            <InputText title="Conta" field="accountNumber" :formName="formName" :required="isBankAccount"
              :maxLength="20" :mask="maskAccountNumber" v-model="bankAccount.accountNumber" />
          </b-col>
          <b-col xs="4" sm="4" md="3" lg="2" xl="2">
            <InputText title="Digito" field="accountDigit" :formName="formName" :required="false" :maxLength="2"
              :mask="maskDigit" v-model="bankAccount.accountDigit" />
          </b-col>
        </b-row>
      </div>

      <div class="bank-account-form-section">
        <span class="bank-account-form-section-title">Identificação</span>
        <b-row>
          <b-col xs="12" sm="12" md="12" lg="6" xl="6">
            <InputText title="Nome" field="name" :formName="formName" :required="true" :maxLength="100"
              :mask="maskName" v-model="bankAccount.name" />
          </b-col>
          <b-col xs="12" sm="12" md="6" lg="3" xl="3">
            <InputDecimal title="Saldo Inicial" field="openingBalance" :formName="formName" :required="false"
              :allowNegative="true" v-model="bankAccount.openingBalance" />
          </b-col>
        </b-row>
      </div>

      <div v-if="showOpenFinance" class="bank-account-form-section">
        <span class="bank-account-form-section-title">Open Finance</span>
        <PluggyConnect :pluggyId="bankAccount.pluggyId" />
      </div>
    </div>
    <br />
  </div>
</template>

<script>

import DotLabel from "@/ui/layout/DotLabel";
import Tooltip from "@/ui/layout/Tooltip";
import Select from "@/ui/forms/Select";
import InputText from "@/ui/forms/InputText";
import InputDecimal from "@/ui/forms/InputDecimal";

import BankCreateUpdate from "../bank/BankCreateUpdate.vue";
import PluggyConnect from "./PluggyConnect.vue";

import BankAccount from "../../models/BankAccount";
import BankAccountService from "../../services/bankAccount.service";

import { mapGetters, mapMutations } from "vuex";

export default {
  name: "BankAccountCreateUpdate",
  props: {
    value: Object,
  },
  components: {
    InputText,
    InputDecimal,
    Select,
    DotLabel,
    Tooltip,
    BankCreateUpdate, PluggyConnect
  },
  data() {
    return {
      id: this.$route.params.id,
      formName: "bankAccountCreateUpdate",
      bankAccount: new BankAccount(),
      numberOfOpenFinance: 0,
      hideBalance: true,
      logoError: false,
    };
  },
  created() {
    if (this.id) this.getById();
    this.getCompanyPlan();
  },
  computed: {
    ...mapGetters("validation", ["isFormValid"]),
    ...mapGetters("generic", ["event", "isLoading"]),
    isBankAccount() {
      if (this.bankAccount.type == 1) return true;
      return false;
    },
    maskAgency() {
      return "#".repeat(10);
    },
    maskAccountNumber() {
      return "#".repeat(20);
    },
    maskDigit() {
      return "XX";
    },
    maskName() {
      return {
        mask: "*".repeat(100),
        tokens: { "*": { pattern: /./ } },
      };
    },
    showOpenFinance() {
      if (!this.id) return false;
      if (!this.isBankAccount) return false;
      return this.numberOfOpenFinance > 0;
    },
    showLogo() {
      return !!this.bankAccount.urlLogo && !this.bankAccount.urlLogo.endsWith("/") && !this.logoError;
    },
    summaryName() {
      if (this.bankAccount.bank && this.bankAccount.bank.content)
        return this.bankAccount.bank.content;
      return this.bankAccount.name;
    },
    accountLabel() {
      if (!this.isBankAccount) return "";

      let agency = this.bankAccount.agency;
      if (agency && this.bankAccount.agencyDigit)
        agency = `${agency}-${this.bankAccount.agencyDigit}`;

      let account = this.bankAccount.accountNumber;
      if (account && this.bankAccount.accountDigit)
        account = `${account}-${this.bankAccount.accountDigit}`;

      let parts = [];
      if (agency) parts.push(`Ag. ${agency}`);
      if (account) parts.push(`Conta ${account}`);

      return parts.join(" · ");
    },
  },
  methods: {
    ...mapMutations("generic", ["removeLoading"]),
    ...mapMutations("validation", ["removeFormDirty", "updateFormDirty", "removeValidation"]),
    selectType(type) {
      if (this.bankAccount.type == type) return;
      this.bankAccount.type = type;
      this.updateFormDirty(true);
      if (type == 2) this.cleanBankValidation();
    },
    cleanBankValidation() {
      let self = this;
      ["bank", "agency", "accountNumber"].forEach(function (field) {
        self.removeValidation({ key: `${field}&${self.formName}` });
      });
    },
    toggleBalance() {
      this.hideBalance = !this.hideBalance;
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    async getCompanyPlan() {
      const response = await BankAccountService.getCompanyPlan();
      this.numberOfOpenFinance = response.content.plan.numberOfOpenFinance || 1;
    },
    async create(closeAfterSave = false) {
      try {
        const response = await BankAccountService.create(this.bankAccount);
        if (response.success) {
          if (closeAfterSave) {
            this.$router.push({ name: "bankAccountListNew" });
            return;
          }
          this.$router.replace({
            name: "bankAccountUpdateNew",
            params: { id: response.content.id },
          });
        }
      } finally {
        this.removeLoading(["saveSaveCancel", "saveAndCloseSaveCancel"]);
      }
    },
    async update(closeAfterSave = false) {
      try {
        const response = await BankAccountService.update(this.bankAccount);
        if (response.success) {
          if (closeAfterSave) {
            this.$router.push({ name: "bankAccountListNew" });
            return;
          }
          this.getById();
        }
      } finally {
        this.removeLoading(["saveSaveCancel", "saveAndCloseSaveCancel"]);
      }
    },
    async getById() {
      try {
        const response = await BankAccountService.getById(this.id);
        this.bankAccount.update(response.content);
      } finally {
        let self = this;
        setTimeout(function () {
          self.removeLoading(["panel"]);
          self.removeFormDirty();
        }, 100);
      }
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name == "saveSaveCancel") {
          if (!this.id) this.create();
          if (this.id) this.update();
        }

        if (event.name == "saveAndCloseSaveCancel") {
          if (!this.id) this.create(true);
          if (this.id) this.update(true);
        }

        if (event.name == "cancelSaveCancel") {
          this.$router.push({ name: "bankAccountListNew" });
        }

        if (event.name == "pluggyUpdated") {
          this.getById();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.bank-account-form {
  margin-right: 10px;
}

.bank-account-form-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.bank-account-form-summary-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.bank-account-form-summary-logo {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: var(--bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand);
  font-size: 18px;
  flex-shrink: 0;
  overflow: hidden;
}

.bank-account-form-summary-logo img {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
}

.bank-account-form-summary-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.bank-account-form-summary-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
}

.bank-account-form-summary-account {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}

.bank-account-form-summary-balance {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 16px;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  font-size: 13px;
}

.bank-account-form-summary-balance-label {
  color: var(--text-secondary);
  white-space: nowrap;
}

.bank-account-form-summary-balance-value {
  font-weight: 600;
  white-space: nowrap;
}

.bank-account-form-summary-balance-hidden {
  color: var(--text-muted);
  font-weight: 600;
  letter-spacing: 2px;
}

.bank-account-form-summary-balance-toggle {
  color: var(--text-muted);
  cursor: pointer;
  transition: color 0.15s;
}

.bank-account-form-summary-balance-toggle:hover {
  color: var(--color-brand);
}

.bank-account-form-positive {
  color: var(--color-success);
}

.bank-account-form-negative {
  color: var(--color-danger);
}

.bank-account-form-card {
  background: var(--bg-base);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 4px 20px 16px 20px;
}

.bank-account-form-section {
  padding: 16px 0;
}

.bank-account-form-section+.bank-account-form-section {
  border-top: 1px solid var(--border-subtle);
}

.bank-account-form-section-title {
  display: block;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  margin-bottom: 14px;
}

.bank-account-form-type {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--bg-base);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}

.bank-account-form-type:hover {
  border-color: var(--color-brand-light);
}

.bank-account-form-type-selected {
  border-color: var(--color-brand);
  background: var(--bg-elevated);
}

.bank-account-form-type-selected .bank-account-form-type-icon {
  background: var(--bg-base);
}

.bank-account-form-type-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: var(--bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand);
  font-size: 16px;
  flex-shrink: 0;
}

.bank-account-form-type-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.bank-account-form-type-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.bank-account-form-type-description {
  font-size: 12px;
  color: var(--text-muted);
  font-style: italic;
}

.bank-account-form-type-check {
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 14px;
  color: var(--color-brand);
}
</style>
